import { Trash2 } from "lucide-react";
import "@app/App.css";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem } from "@comps/ui/form";
import { Button } from "@comps/ui/button";
import { del } from "@utils/utils";
import { useEffect, useState } from "react";
import { useModelStore } from "src/store";
import { useTranslation } from "react-i18next";
import Field, { FieldType, TYPE_ITEMS_RELATION_KEY } from "@dto/fields";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_CheckboxCell_not_selected, TW_CheckboxCell_selected, TW_FieldMenuItem, useHover } from "@utils/twClasses";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@comps/ui/input";
import { translate } from "src/i18n/i18n";
import { idToKey } from "@utils/types";
import { BASE_URL } from "@utils/constants";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { toastError } from "@utils/toasts";
import { removeRelationFromObjectInfos } from "@utils/columnErrors";

interface FieldMenuDeleteItemProps {
  field: Field;
  onFieldDelete: (field: Field) => void;
  closeMenu: () => void;
}

function FieldMenuDeleteItem({ field, onFieldDelete, closeMenu }: FieldMenuDeleteItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [lastFieldOnRelation, setLastFieldOnRelation] = useState<boolean>(false);
  const objectInfos = useModelStore(state => state.selectedObjectInfos);
  const fieldByKeys = useModelStore(state => state.fieldByKeys);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const hover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));

  useEffect(() => {
    if (field.type === FieldType.Link) {
      const relationKey = field.typeItems?.get(TYPE_ITEMS_RELATION_KEY);
      if (relationKey && fieldByKeys) {
        const fieldsOnSameRelation = Array.from(fieldByKeys.values()).filter(field => field.type === FieldType.Link && field.typeItems?.get(TYPE_ITEMS_RELATION_KEY) === relationKey);
        setLastFieldOnRelation(fieldsOnSameRelation.length === 1);
      }
    }
  }, [field, objectInfos]);

  const fieldDeleted = (field: Field) => {
    onFieldDelete(field);
    setIsDialogOpen(false);
    closeMenu();
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement> | React.FocusEvent<HTMLDivElement>) {
    event.preventDefault();
    closeMenu();
    setIsDialogOpen(false);
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger className={TW_FieldMenuItem + ` ${field === null ? "item-disabled" : ""}`} {...hover}><Trash2 className="FieldMenuItemIcon" />{t("field.button.delete.text")}</DialogTrigger>
      <DialogContent onOpenAutoFocus={(event) => event.preventDefault()} onBlur={cancel}>
        <DialogHeader>
          <DialogTitle>{t("field.dialog.delete.title")}</DialogTitle>
          <DialogDescription>{t("field.dialog.delete.description")}</DialogDescription>
        </DialogHeader>
        {field && <FieldDeleteForm initialField={field} lastFieldOnRelation={lastFieldOnRelation} onFieldDelete={fieldDeleted} onCancel={cancel} />}
      </DialogContent>
    </Dialog>
  )
}

interface FieldDeleteFormProps {
  initialField: Field;
  lastFieldOnRelation: boolean;
  onFieldDelete: (field: Field) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function FieldDeleteForm({ initialField, lastFieldOnRelation, onFieldDelete, onCancel }: FieldDeleteFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const objectInfos = useModelStore(state => state.selectedObjectInfos);
  const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
  const relationByKeys = useModelStore(state => state.relationByKeys);
  const objectByKeys = useModelStore(state => state.objectByKeys);
  const updateSelectedObjectInfos = useModelStore(state => state.updateSelectedObjectInfos);
  const updateObjectInfos = useModelStore(state => state.updateObjectInfos);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const confirmHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const formSchema = z.object({
    deleteRelation: z.boolean()
  })
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      deleteRelation: false,
    },
  })


  function onSubmit(values: z.infer<typeof formSchema>) {

    if (workspace === null || objectInfos === null) {
      console.error("No workspace selected");
      return;
    }

    del<void>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${objectInfos.object._key}/fields/${initialField._key}`, null).then(response => {
      if (isCompozzError(response)) {
        const cError = response as CompozzError;
        if (cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else {
          toastError(cError.message);
        }
        return;
      }

      const relationKey = initialField.typeItems?.get(TYPE_ITEMS_RELATION_KEY);
      if (values.deleteRelation && relationKey) {
        del(`${BASE_URL}/model/workspaces/${workspace._key}/relations/${relationKey}`, null).then(response => {
          const newSelectedObjectInfos = {
            ...objectInfos,
            fields: objectInfos.fields?.filter(f => f._key !== initialField._key) ?? [],
            relations: objectInfos.relations?.filter(relation => relation._key !== relationKey) ?? []
          };
          // The relation is gone for every object, not only for this one:
          // the link fields through it on the other objects become invalid
          const allObjectInfos = removeRelationFromObjectInfos(useModelStore.getState().objectInfos, relationKey)
            .map(objectInfo => objectInfo.object._key === newSelectedObjectInfos.object._key ? newSelectedObjectInfos : objectInfo);
          updateObjectInfos(allObjectInfos, newSelectedObjectInfos, null, null, sharedObjectInfos);
          onFieldDelete(initialField);
        });
      } else {
        const newSelectedObjectInfos = {
          ...objectInfos,
          fields: objectInfos.fields?.filter(f => f._key !== initialField._key) ?? [],
        };
        updateSelectedObjectInfos(newSelectedObjectInfos, null, null, sharedObjectInfos);
        onFieldDelete(initialField);
      }
    })
  }

  const relationKey = initialField.typeItems?.get(TYPE_ITEMS_RELATION_KEY);
  const relation = relationByKeys?.get(relationKey);
  let relationName: string | undefined = undefined;
  let objectName: string | undefined = undefined;
  let linkedObjectName: string | undefined = undefined;
  if (relation && objectByKeys) {
    relationName = relation.name;
    objectName = objectByKeys.get(idToKey(relation._from))?.object.name;
    linkedObjectName = objectByKeys.get(idToKey(relation._to))?.object.name;
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {lastFieldOnRelation && relationName && objectName && linkedObjectName &&
          <FormField
            control={form.control}
            name="deleteRelation"
            render={({ field }) => (
              <FormItem>
                <p>{translate(t, "field.dialog.delete.relation.confirmation", [relationName, objectName, linkedObjectName])}</p>
                <FormControl>
                  <Input className={(field.value ? TW_CheckboxCell_selected : TW_CheckboxCell_not_selected)} type="checkbox" checked={field.value} onChange={() => form.setValue("deleteRelation", !field.value)} />
                </FormControl>
              </FormItem>
            )}
          />
        }
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...confirmHover}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default FieldMenuDeleteItem;
